import type { FourLightsWithOptionalFan } from "building-types/electrical/electricalLayoutTypes";
import { ElectricalLayoutType, RoomBoundaryType } from "building-types/electrical/electricalLayoutTypesEnum";
import { LoadTypeSlot } from "building-types/electrical/electricalSlots";
import {
  ControllerSelectionType,
  ControllerSwitchType,
  ControllerType,
} from "building-types/electrical/electricalTypesEnum";
import type { GeneratedElectricalGroup, Load } from "building-types/electrical/generatedElectricalTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { getLoadCatalog } from "doc/getLoadCatalog";
import { makeBuiltInLabelTable } from "doc/utils/labelTools";
import { aabb2ByPoints, aabb2Center } from "geom/aabb2";
import { Vec2PolyLine2ContainmentClass, classifyContainmentVec2PolyLine2 } from "geom/containment";
import { poleOfInaccessibilityPolyLine2 } from "geom/label";
import { offsetPolyLine2 } from "geom/offset";
import {
  polyLine2AABB2,
  polyLine2Centroid,
  polyLine2Copy,
  polyLine2Vertex,
  polyLine2VertexCount,
} from "geom/polyLine2";
import { polyLine2MaximalRectangle } from "geom/polyLine2MaximalRect";
import type { Vec2 } from "geom/vec2";
import { vec2Copy, vec2Dist } from "geom/vec2";
import { commitControllerSelection } from "../controllerSelection/controllerSelectionCommit";
import type { ElectricalLayoutFunc, ElectricalLayoutOpts } from "../electricalLayoutTypes";
import { loadTypeFromSlots } from "../loadTypeFromSlots";

export function fourLightsWithOptionalFan({
  addFan = false,
  controllerPlacementType = [
    {
      type: ControllerSelectionType.PRIMARY_ENTRANCE,
    },
  ],
  fourLightsLoadSlot = LoadTypeSlot.PRIMARY_LIGHT,
  loadType: fourLightsLoadType,
  cornerInset = 36,
  roomBoundaryType,
}: FourLightsWithOptionalFan): ElectricalLayoutFunc {
  const resolvedRoomBoundaryType = roomBoundaryType === undefined ? RoomBoundaryType.BOUNDING_BOX : roomBoundaryType;

  return async (opts: ElectricalLayoutOpts) => {
    const { room, controllerSelectionFunc, elecSlots, mirror, electricalItemCatalogSettings } = opts;
    const resolvedLoadType = fourLightsLoadType ?? loadTypeFromSlots(fourLightsLoadSlot, elecSlots);
    const loadCatalog = getLoadCatalog(electricalItemCatalogSettings);
    const loadDescriptor = loadCatalog[resolvedLoadType];
    const { outline: roomOutline } = room;

    let outline = polyLine2Copy(roomOutline);
    const lightLocs: Vec2[] = [];

    switch (resolvedRoomBoundaryType) {
      case RoomBoundaryType.BOUNDING_BOX: {
        const { minX, minY, maxX, maxY } = polyLine2AABB2(outline);
        if (mirror) {
          lightLocs.push([minX + cornerInset, maxY - cornerInset]);
          lightLocs.push([maxX - cornerInset, maxY - cornerInset]);
          lightLocs.push([maxX - cornerInset, minY + cornerInset]);
          lightLocs.push([minX + cornerInset, minY + cornerInset]);
        } else {
          lightLocs.push([minX + cornerInset, minY + cornerInset]);
          lightLocs.push([maxX - cornerInset, minY + cornerInset]);
          lightLocs.push([maxX - cornerInset, maxY - cornerInset]);
          lightLocs.push([minX + cornerInset, maxY - cornerInset]);
        }
        break;
      }
      case RoomBoundaryType.MAX_RECTANGLE:
        {
          const { maxRectAsPolyLine2 } = polyLine2MaximalRectangle(outline);
          outline = maxRectAsPolyLine2;
          outline = offsetPolyLine2(outline, -cornerInset);

          for (let i = 0; i < polyLine2VertexCount(outline) - 1; i++) {
            const [x, y] = polyLine2Vertex(outline, i);
            lightLocs.push([x, y]);
          }
        }
        break;
    }

    const lightsBB = aabb2ByPoints(mirror ? lightLocs : lightLocs);
    const lightCenter = aabb2Center(lightsBB);

    const candidate = await controllerSelectionFunc(controllerPlacementType, lightCenter);
    if (!candidate) {
      // console.warn("no controllers assigned");
      return [];
    }

    const controllerPosition = commitControllerSelection(
      candidate,
      ElectricalLayoutType.FOUR_LIGHTS,
      ControllerType.SWITCH,
      lightCenter,
    );

    const {
      position: [contX, contY],
    } = ensureDefined(controllerPosition);

    let closestIndex = 0;
    let minDist = 10000;
    for (const iString of Object.keys(lightLocs)) {
      const i = parseInt(iString);
      const lightLoc = lightLocs[i];
      const [lX, lY] = lightLoc;
      const dist = vec2Dist(lX, lY, contX, contY);
      if (dist < minDist) {
        closestIndex = i;
        minDist = dist;
      }
    }

    const loads: Load[] = [];
    for (let i = 0; i < lightLocs.length; i++) {
      const loc = lightLocs[(i + closestIndex) % lightLocs.length];
      loads.push({
        loadType: resolvedLoadType,
        loadDescriptor,
        position: vec2Copy(loc),
        normal: [1, 0],
        levelId: room.level.id,
        sourceRoomHandle: room.roomHandle,
      });
    }

    const lightElectricalGroup: GeneratedElectricalGroup = {
      levelId: opts.room.level.id,
      loads,
      controllers: [
        {
          type: ControllerType.SWITCH,
          switchType: ControllerSwitchType.STANDARD,
          position: controllerPosition,
          loadIndex: 0,
          levelId: room.level.id,
          sourceRoomHandle: room.roomHandle,
          candidateId: candidate?.candidate?.id,
          labels: makeBuiltInLabelTable("addToElectricalPlan"),
        },
      ],
    };

    if (!addFan) {
      return [lightElectricalGroup];
    }

    const fanLocation: number[] = [];

    const [cX, cY] = ensureDefined(polyLine2Centroid(outline));
    if (classifyContainmentVec2PolyLine2(cX, cY, outline) === Vec2PolyLine2ContainmentClass.INSIDE) {
      fanLocation.push(cX);
      fanLocation.push(cY);
    } else {
      const [pX, pY] = ensureDefined(poleOfInaccessibilityPolyLine2(outline));
      fanLocation.push(pX);
      fanLocation.push(pY);
    }

    const fanCandidate = await controllerSelectionFunc(controllerPlacementType, lightCenter);
    if (!fanCandidate) {
      console.warn("no fan controller assigned");
      return [];
    }

    const fanControllerPosition = commitControllerSelection(
      fanCandidate,
      ElectricalLayoutType.FOUR_LIGHTS,
      ControllerType.SWITCH,
      [cX, cY],
    );

    const ceilingFanLoadType = loadTypeFromSlots(LoadTypeSlot.FAN_CEILING, elecSlots);

    const fanElectricalGroup: GeneratedElectricalGroup = {
      levelId: opts.room.level.id,
      loads: [
        {
          loadType: ceilingFanLoadType,
          loadDescriptor: loadCatalog[ceilingFanLoadType],
          position: [cX, cY],
          normal: [1, 0],
          levelId: room.level.id,
          sourceRoomHandle: room.roomHandle,
        },
      ],
      controllers: [
        {
          type: ControllerType.SWITCH,
          switchType: ControllerSwitchType.STANDARD,
          position: fanControllerPosition,
          loadIndex: 0,
          levelId: room.level.id,
          sourceRoomHandle: room.roomHandle,
          candidateId: fanCandidate?.candidate?.id,
          labels: makeBuiltInLabelTable("addToElectricalPlan"),
        },
      ],
    };

    return [lightElectricalGroup, fanElectricalGroup];
  };
}
